import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import type { Platform } from '@/types/qriots';
import { RegionPickerModal } from './RegionPickerModal';

const TAGLINE_RE = /^[A-Za-z0-9]{3,5}$/;

export interface CompareSideValue {
  gameName: string;
  tagLine: string;
  region?: Platform;
}

interface CompareSideInputProps {
  label: string;
  sublabel?: string;
  value: CompareSideValue;
  onChange: (next: CompareSideValue) => void;
}

export function CompareSideInput({
  label,
  sublabel,
  value,
  onChange,
}: CompareSideInputProps) {
  const { t } = useTranslation('riot');
  const [pickerOpen, setPickerOpen] = useState(false);

  // Allow paste of "Foo#BAR1" into gameName and auto-split.
  const onGameNameChange = (raw: string) => {
    if (raw.includes('#')) {
      const [gn, tl] = raw.split('#');
      onChange({
        gameName: (gn ?? '').slice(0, 16),
        tagLine: (tl ?? '').replace(/[^A-Za-z0-9]/g, '').slice(0, 5),
        region: value.region,
      });
    } else {
      onChange({
        gameName: raw.slice(0, 16),
        tagLine: value.tagLine,
        region: value.region,
      });
    }
  };

  const taglineValid = TAGLINE_RE.test(value.tagLine.trim());

  return (
    <div className="mx-4 flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-bold uppercase tracking-wider text-fg-muted">
          {label}
        </h2>
        {sublabel && (
          <span className="text-[10px] text-fg-muted">{sublabel}</span>
        )}
      </div>

      <div className="flex items-stretch gap-0 overflow-hidden rounded-lg border border-border bg-bg-secondary focus-within:border-brand">
        <input
          type="text"
          value={value.gameName}
          onChange={(e) => onGameNameChange(e.target.value)}
          placeholder={t('field.gameName')}
          maxLength={16}
          className="flex-1 bg-transparent px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted focus:outline-none"
        />
        <span aria-hidden className="flex select-none items-center px-1 text-sm font-medium text-fg-muted">
          #
        </span>
        <input
          type="text"
          value={value.tagLine}
          onChange={(e) =>
            onChange({
              ...value,
              tagLine: e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 5),
            })
          }
          placeholder={t('field.tagLine')}
          maxLength={5}
          className="w-24 bg-transparent px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted focus:outline-none"
        />
      </div>

      {value.tagLine.length > 0 && !taglineValid && (
        <p className="text-xs text-danger">{t('field.error.taglineFormat')}</p>
      )}

      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        className="flex items-center justify-between rounded-lg border border-border bg-bg-tertiary px-3 py-2 text-sm text-fg active:opacity-80"
      >
        <span className="text-fg-muted">{t('compare.regionLabel')}</span>
        <span className="flex items-center gap-1 font-medium">
          {value.region ?? '—'}
          <ChevronDown aria-hidden className="h-4 w-4" />
        </span>
      </button>

      {pickerOpen && (
        <RegionPickerModal
          current={value.region}
          onPick={(p) => {
            onChange({ ...value, region: p });
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
